import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import api from '../../api';
import { QUALITY_SERVICE_API_URL } from '../../config';
import { useCurrentUser } from '../../context/CurrentUserContext';
import RoleGate from '../RoleGate';

const BASE_PATH = `${QUALITY_SERVICE_API_URL}/hub/mail-forwarder-blocked-senders`;

function AdminBlockedSenders() {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const isAdmin = currentUser?.hubRole === 'admin';

  const [blockedSenders, setBlockedSenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [email, setEmail] = useState('');
  const [scope, setScope] = useState('personal');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    api.get(BASE_PATH)
      .then(r => setBlockedSenders(r.data))
      .catch(() => setError('Failed to load blocked senders.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;

    setAdding(true);
    setAddError(null);
    try {
      await api.post(BASE_PATH, {
        senderAddress: trimmed,
        scope: isAdmin ? scope : 'personal',
      });
      setEmail('');
      setScope('personal');
      load();
    } catch (err) {
      if (err.response?.status === 409) {
        setAddError('This sender address is already blocked.');
      } else {
        setAddError(err.response?.data?.message || 'Failed to block sender. Please try again.');
      }
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (entry) => {
    if (!window.confirm(`Unblock "${entry.senderAddress}"?`)) return;
    try {
      await api.delete(`${BASE_PATH}/${entry.id}`);
      load();
    } catch {
      setError('Failed to unblock sender.');
    }
  };

  const canManage = (entry) => {
    if (!currentUser) return false;
    if (isAdmin) return true;
    return entry.scope === 'personal' && entry.hubUser?.id === currentUser.id;
  };

  return (
    <div className="admin-campaigns">
      <div className="admin-section-toolbar">
        <button className="admin-btn-back" onClick={() => navigate('/admin/mail-forwarders')}>
          <ArrowLeft size={16} /> Back
        </button>
        <h2 className="admin-section-title">Blocked Senders</h2>
      </div>

      <form className="admin-filter-bar" onSubmit={handleAdd}>
        <div className="admin-filter-group" style={{ flex: 1 }}>
          <label className="admin-filter-label" htmlFor="bs-email">Sender email to block</label>
          <input
            id="bs-email"
            className="cf-input"
            type="email"
            required
            placeholder="spammer@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
        </div>
        <RoleGate allow={['admin']}>
          <div className="admin-filter-group">
            <label className="admin-filter-label" htmlFor="bs-scope">Scope</label>
            <select id="bs-scope" className="cf-input" value={scope} onChange={e => setScope(e.target.value)}>
              <option value="personal">Just for me</option>
              <option value="global">For everyone</option>
            </select>
          </div>
        </RoleGate>
        <div className="admin-filter-actions">
          <button type="submit" className="admin-btn-primary" disabled={adding || !email.trim()}>
            {adding ? 'Blocking...' : 'Block Sender'}
          </button>
        </div>
      </form>

      {addError && <div className="admin-error">{addError}</div>}
      {error && <div className="admin-error">{error}</div>}

      {loading ? (
        <div className="admin-loading">Loading...</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Sender Address</th>
              <th>Scope</th>
              <th>Blocked By</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {blockedSenders.map(entry => (
              <tr key={entry.id}>
                <td className="admin-td-name">{entry.senderAddress}</td>
                <td>
                  <span className={entry.scope === 'global' ? 'atm-active-badge' : 'admin-label-chip'}>
                    {entry.scope === 'global' ? 'Everyone' : 'Personal'}
                  </span>
                </td>
                <td>{entry.hubUser?.email ?? '—'}</td>
                <td className="mfr-td-actions">
                  {canManage(entry) && (
                    <button className="admin-btn-delete" onClick={() => handleRemove(entry)}>Unblock</button>
                  )}
                </td>
              </tr>
            ))}
            {blockedSenders.length === 0 && (
              <tr><td colSpan={4} className="admin-empty">No blocked senders yet.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default AdminBlockedSenders;
